import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadFile, NzUploadModule, NzUploadChangeParam } from 'ng-zorro-antd/upload';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'tenant-authentication',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NzCardModule,
    NzButtonModule,
    NzUploadModule,
    NzIconModule,
    NzInputModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './tenant-authentication.component.html',
  styleUrls: ['./tenant-authentication.component.less']
})
export class TenantAuthenticationComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly cdr = inject(ChangeDetectorRef);

  tenantId = '';
  submitting = false;
  certificateStatus: 'Audit' | 'Audited' | 'Back' | '' = '';
  certificateFailReason = '';
  existingCertificateFileId = '';
  existingCertificateFileName = '';
  existingCertificateFileSizeText = '';
  existingCertificatePreviewUrl = '';
  fileList: NzUploadFile[] = [];
  maxSizeBytes = 10 * 1024 * 1024;
  acceptTypes = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
  tenantName = '';
  unifiedNumber = '';
  representativeNumber = '';
  selectedFile: File | null = null;
  showErrors = false;

  get canEdit(): boolean {
    return !this.certificateStatus || this.certificateStatus === 'Back';
  }

  ngOnInit(): void {
    this.tenantId = this.route.snapshot.paramMap.get('id') || '';
    this.loadTenantDetail();
  }

  private loadTenantDetail(): void {
    if (!this.tenantId) return;
    this.http.post(`${ConstantUtil.ADMIN_TENANT_ITEM}/${this.tenantId}`, {}).subscribe(res => {
      const safeRes: any = res || {};
      const data: any = safeRes.data ?? safeRes;
      const tenant: any = data.tenantModel ?? data.tenant ?? data.tenantInfo ?? data;
      if (safeRes?.success && tenant) {
        this.tenantName = tenant?.name ?? '';
        this.unifiedNumber = tenant?.unifiedNumber ?? '';
        this.representativeNumber = tenant?.representativeNumber ?? '';
        this.certificateStatus = tenant?.certificateStatus ?? '';
        this.certificateFailReason = tenant?.certificateFailReason ?? '';
        const directFileId = String(tenant?.certificateFileId ?? data?.certificateFileId ?? '').trim();
        const directFileName = String(data?.certificateFileName ?? tenant?.certificateFileName ?? '').trim();
        const directFileSize = data?.certificateFileSize ?? tenant?.certificateFileSize;
        const directPreviewUrl = String(data?.certificateFileViewUrl ?? '').trim();
        this.existingCertificateFileId = directFileId;
        this.existingCertificateFileName = directFileName;
        this.existingCertificateFileSizeText = this.formatFileSize(directFileSize);
        this.existingCertificatePreviewUrl = directPreviewUrl;
        this.cdr.detectChanges();
      }
    });
  }

  private formatFileSize(value: unknown): string {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1) {
      size /= 1024;
      idx += 1;
    }
    const fixed = idx === 0 ? String(Math.round(size)) : size.toFixed(1);
    return `${fixed} ${units[idx]}`;
  }

  beforeUpload = (file: NzUploadFile, _fileList: NzUploadFile[]): boolean => {
    // 兼容处理：尝试获取原始文件对象，如果是原生 File 对象则直接使用，否则从 originFileObj 获取
    const raw = (file instanceof File ? file : file.originFileObj) as File;
    const size = raw ? raw.size : Number(file.size || 0);
    const name = (raw ? raw.name : file.name) || '';
    if (size > this.maxSizeBytes) {
      this.message.warning(this.i18n.fanyi('tenant.certification.file-too-large') || '文件大小不能超过10MB');
      return false;
    }
    const ext = name.toLowerCase();
    const ok =
      ext.endsWith('.pdf') ||
      ext.endsWith('.jpg') ||
      ext.endsWith('.jpeg') ||
      ext.endsWith('.png') ||
      ext.endsWith('.doc') ||
      ext.endsWith('.docx');
    if (!ok) {
      this.message.warning(this.i18n.fanyi('tenant.certification.file-type-invalid') || '文件类型不支持');
      return false;
    }
    this.fileList = [file];
    this.selectedFile = raw || null;
    return false;
  };

  removeFile = (file: NzUploadFile): boolean => {
    this.fileList = this.fileList.filter(f => f.uid !== file.uid);
    // 重置 selectedFile
    if (this.fileList.length === 0) {
      this.selectedFile = null;
    } else {
      const item = this.fileList[0];
      this.selectedFile = (item instanceof File ? item : item.originFileObj) as File | null;
    }
    return false;
  };

  onUploadChange = (param: NzUploadChangeParam): void => {
    // 仅用于更新文件列表视图，核心文件对象由 beforeUpload 捕获
    this.fileList = (param?.fileList || []).slice(0, 1);
  };

  previewExistingFile(): void {
    const url = (this.existingCertificatePreviewUrl || '').trim();
    if (!url) {
      this.message.error(this.i18n.fanyi('tenant.certification.preview-url-missing') || '未找到预览链接');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  clearExistingFile(): void {
    this.existingCertificateFileId = '';
    this.existingCertificateFileName = '';
    this.existingCertificateFileSizeText = '';
    this.existingCertificatePreviewUrl = '';
  }

  submit(): void {
    if (!this.canEdit) return;
    this.showErrors = true;
    if (!this.tenantId) {
      this.message.error(this.i18n.fanyi('tenant.certification.tenant-id-required') || '未获取到租户ID');
      return;
    }
    if (!this.unifiedNumber?.trim()) {
      this.message.warning(this.i18n.fanyi('tenant.certification.unified-required') || '请输入统一社会信用代码');
      return;
    }
    if (!this.representativeNumber?.trim()) {
      this.message.warning(this.i18n.fanyi('tenant.certification.representative-required') || '请输入法人代表号码');
      return;
    }
    if (!this.tenantName?.trim()) {
      this.message.warning(this.i18n.fanyi('tenant.certification.tenant-name-required') || '请输入租户名称');
      return;
    }

    // 尝试从多个来源获取文件对象：selectedFile 缓存、fileList 中的 originFileObj
    let finalFile = this.selectedFile;
    if (!finalFile && this.fileList.length > 0) {
      const item = this.fileList[0];
      if (item instanceof File) {
        finalFile = item;
      } else if (item.originFileObj) {
        finalFile = item.originFileObj as File;
      }
    }

    if (!this.fileList.length || !finalFile) {
      this.message.warning(this.i18n.fanyi('tenant.certification.file-required') || '请先上传认证文件');
      return;
    }
    this.submitting = true;
    this.cdr.markForCheck();
    const args = {
      name: this.tenantName,
      unifiedNumber: this.unifiedNumber,
      representativeNumber: this.representativeNumber
    };
    this.http
      .postFormData(ConstantUtil.TENANT_V1_CERTIFICATE, args, finalFile as File, 'certificateFile')
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('tenant.certification.submit-success') || '提交成功');
            this.certificateStatus = 'Audit';
            this.certificateFailReason = '';
          } else {
            showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.certification.submit-failed') || '提交失败');
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.message.error(this.i18n.fanyi('tenant.certification.submit-failed') || '提交失败');
          this.cdr.markForCheck();
        }
      });
  }
}
